import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PhotoUploader } from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  name: "", listing_type: "business", owner_name: "", category_slug: "", city_slug: "etah", address: "", service_area: "", pricing: "", phone: "", whatsapp: "",
  maps_url: "", description: "", services: "", price_range: "₹₹", website: "", instagram: "", facebook: "",
  photos: [], verified: false, featured: false, status: "published",
};

export function BusinessForm({ open, onOpenChange, categories, cities, serviceCategories = [], listingType = "business", editing, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setF({ ...EMPTY, ...editing, listing_type: editing.listing_type || "business", services: (editing.services || []).join(", ") });
    } else {
      setF({ ...EMPTY, listing_type: listingType });
    }
  }, [editing, open, listingType]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isService = f.listing_type === "service";
  const catList = isService ? serviceCategories : categories;

  const save = async (publish) => {
    if (!f.name || !f.category_slug || !f.city_slug) { toast.error("Name, category and city are required"); return; }
    setSaving(true);
    const payload = {
      ...f,
      listing_type: f.listing_type || "business",
      services: typeof f.services === "string" ? f.services.split(",").map((s) => s.trim()).filter(Boolean) : f.services,
      status: publish ? "published" : "draft",
      menu: f.menu || [],
    };
    try {
      if (editing) {
        await api.put(`/admin/businesses/${editing.id}`, payload);
        toast.success("Business updated");
      } else {
        await api.post("/admin/businesses", payload);
        toast.success(publish ? "Business published" : "Saved as draft");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not save business");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="business-form-modal">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "Add"} {isService ? "Service Provider" : "Business"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <F label="Listing Type">
            <Select value={f.listing_type} onValueChange={(v) => setF((s) => ({ ...s, listing_type: v, category_slug: "" }))}>
              <SelectTrigger className="rounded-xl h-11" data-testid="bf-listing-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="business">Business</SelectItem><SelectItem value="service">Service Provider</SelectItem></SelectContent>
            </Select>
          </F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Name *"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl h-11" data-testid="bf-name" /></F>
            <F label="Owner Name"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="rounded-xl h-11" data-testid="bf-owner" /></F>
            <F label="Category *">
              <Select value={f.category_slug} onValueChange={(v) => set("category_slug", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="bf-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{catList.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="City *">
              <Select value={f.city_slug} onValueChange={(v) => set("city_slug", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="bf-city"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{cities.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-xl h-11" data-testid="bf-phone" /></F>
            <F label="WhatsApp"><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="rounded-xl h-11" data-testid="bf-whatsapp" /></F>
            <F label="Price Range">
              <Select value={f.price_range} onValueChange={(v) => set("price_range", v)}>
                <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="₹">₹</SelectItem><SelectItem value="₹₹">₹₹</SelectItem><SelectItem value="₹₹₹">₹₹₹</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Website"><Input value={f.website} onChange={(e) => set("website", e.target.value)} className="rounded-xl h-11" data-testid="bf-website" /></F>
          </div>
          <F label="Address"><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="rounded-xl h-11" data-testid="bf-address" /></F>
          {isService && (
            <div className="grid sm:grid-cols-2 gap-3">
              <F label="Service Area"><Input value={f.service_area} onChange={(e) => set("service_area", e.target.value)} className="rounded-xl h-11" data-testid="bf-service-area" /></F>
              <F label="Pricing (optional)"><Input value={f.pricing} onChange={(e) => set("pricing", e.target.value)} className="rounded-xl h-11" placeholder="e.g. From ₹199 / visit" data-testid="bf-pricing" /></F>
            </div>
          )}
          <F label="Google Maps URL"><Input value={f.maps_url} onChange={(e) => set("maps_url", e.target.value)} className="rounded-xl h-11" data-testid="bf-maps" /></F>
          <F label="Description"><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className="rounded-xl" rows={2} data-testid="bf-desc" /></F>
          <F label="Services (comma separated)"><Input value={f.services} onChange={(e) => set("services", e.target.value)} className="rounded-xl h-11" data-testid="bf-services" /></F>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Instagram"><Input value={f.instagram} onChange={(e) => set("instagram", e.target.value)} className="rounded-xl h-11" /></F>
            <F label="Facebook"><Input value={f.facebook} onChange={(e) => set("facebook", e.target.value)} className="rounded-xl h-11" /></F>
          </div>
          <F label="Photos"><PhotoUploader value={f.photos} onChange={(v) => set("photos", v)} /></F>
          <div className="flex gap-6 pt-1">
            <div className="flex items-center gap-2"><Switch checked={f.verified} onCheckedChange={(v) => set("verified", v)} data-testid="bf-verified" /><Label>Verified</Label></div>
            <div className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} data-testid="bf-featured" /><Label>Featured</Label></div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-full flex-1" data-testid="bf-save-draft">Save as Draft</Button>
            <Button onClick={() => save(true)} disabled={saving} className="rounded-full flex-1 font-semibold" data-testid="bf-publish">{editing ? "Save Changes" : "Publish"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm font-semibold">{label}</Label>{children}</div>;
}
